class Logger {
  private static instance: Logger;
  private isProduction: boolean;

  private constructor() {
    // Check if we're in production environment
    this.isProduction = import.meta.env.VITE_APP_DOMAIN === 'https://simplevision.app';
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private formatMessage(level: string, message: string, data?: unknown): string {
    const timestamp = new Date().toISOString();
    let logMessage = `[${timestamp}] ${level}: ${message}`;
    if (data) {
      logMessage += `\nData: ${JSON.stringify(data, null, 2)}`;
    }
    return logMessage + '\n';
  }

  private writeToConsole(level: string, message: string, data?: unknown) {
    // Skip all logs in production
    if (this.isProduction) {
      return;
    }

    const formattedMessage = this.formatMessage(level, message, data);

    // Write to browser console with appropriate level
    switch (level) {
      case 'DEBUG':
        console.debug(formattedMessage);
        break;
      case 'INFO':
        console.info(formattedMessage);
        break;
      case 'WARN':
        console.warn(formattedMessage);
        break;
      case 'ERROR':
        console.error(formattedMessage);
        break;
    }

    // Store logs in localStorage for persistence (only in development)
    try {
      const logs = JSON.parse(localStorage.getItem('vr-debug-logs') || '[]');
      logs.push({
        timestamp: new Date().toISOString(),
        level,
        message,
        data,
      });
      // Keep only last 1000 logs
      if (logs.length > 1000) {
        logs.shift();
      }
      localStorage.setItem('vr-debug-logs', JSON.stringify(logs));
    } catch (error) {
      console.error('Failed to store logs:', error);
    }
  }

  public debug(message: string, data?: unknown) {
    this.writeToConsole('DEBUG', message, data);
  }

  public info(message: string, data?: unknown) {
    this.writeToConsole('INFO', message, data);
  }

  public warn(message: string, data?: unknown) {
    this.writeToConsole('WARN', message, data);
  }

  public error(message: string, data?: unknown) {
    this.writeToConsole('ERROR', message, data);
  }

  // Method to retrieve stored logs
  public getStoredLogs(): unknown[] {
    try {
      return JSON.parse(localStorage.getItem('vr-debug-logs') || '[]');
    } catch (error) {
      console.error('Failed to retrieve logs:', error);
      return [];
    }
  }

  // Method to clear stored logs
  public clearStoredLogs(): void {
    try {
      localStorage.removeItem('vr-debug-logs');
    } catch (error) {
      console.error('Failed to clear logs:', error);
    }
  }
}

export const logger = Logger.getInstance();
